import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";
import { Button, Form, Input, Divider } from "antd";
import swal from "sweetalert";
import {signin, signinByMicrosoft} from "services/auth";
import axios from "axios";
import "./signin.css";

export const MICROSOFT_CONFIG = {
    clientId: process.env.REACT_APP_MICROSOFT_CLIENT_ID,
    tenantId: process.env.REACT_APP_MICROSOFT_TENANT_ID || 'common',
    redirectUri: process.env.REACT_APP_MICROSOFT_REDIRECT_URI,
    scope: 'openid profile email User.Read',
};

// PKCE Helper Functions
function generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
}

function base64UrlEncode(buffer) {
    const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function generateCodeChallenge(verifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(hash);
}

async function exchangeCodeForToken(code) {
    const tokenEndpoint = `https://login.microsoftonline.com/${MICROSOFT_CONFIG.tenantId}/oauth2/v2.0/token`;

    // Retrieve the code verifier from session storage
    const codeVerifier = sessionStorage.getItem('pkce_code_verifier');

    if (!codeVerifier) {
        throw new Error('PKCE code verifier not found. Please try logging in again.');
    }

    const params = new URLSearchParams({
        client_id: MICROSOFT_CONFIG.clientId,
        code: code,
        redirect_uri: MICROSOFT_CONFIG.redirectUri,
        grant_type: 'authorization_code',
        code_verifier: codeVerifier, // Required for PKCE
    });

    try {
        const response = await axios.post(tokenEndpoint, params.toString(), {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });

        // Clear the code verifier after successful exchange
        sessionStorage.removeItem('pkce_code_verifier');

        return response.data;
    } catch (error) {
        console.error('Token exchange failed:', error.response?.data);
        throw new Error(
            error.response?.data?.error_description ||
            'Failed to exchange authorization code for token'
        );
    }
}

export default function UserAuthentication() {
    const [loading, setLoading] = useState(false);
    const [msLoading, setMsLoading] = useState(false);
    const navigate = useNavigate();
    const userNameInputRef = React.useRef(null);
    const [form] = Form.useForm();

    useEffect(() => {
        userNameInputRef.current.focus();
    }, []);

    const getAllUrlParams = () => {
        const params = new URLSearchParams(window.location.search);
        const urlParams = {};

        for (const [key, value] of params.entries()) {
            urlParams[key] = value;
        }

        return urlParams;
    };

    useEffect(() => {
        const allParams = getAllUrlParams();
        if (allParams.code) {
            handleMicrosoftCallback(allParams.code);
        } else if (allParams.error) {
            swal({
                title: "Microsoft login failed",
                text: allParams.error_description || allParams.error,
                icon: "error",
                timer: 3000,
                buttons: false,
            });
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMicrosoftCallback = async (code) => {
        try {
            setMsLoading(true);

            console.log('Processing Microsoft callback with code...');

            // Exchange code for tokens using PKCE
            const tokenResponse = await exchangeCodeForToken(code);

            console.log('Token exchange successful');

            // Send Microsoft access token to backend for secure verification
            // Backend will verify the token with Microsoft and extract user info
            const response = await signinByMicrosoft(tokenResponse.access_token);

            if (response) {
                const data = response.data.data;
                // Add Microsoft login flag to session data
                data.loginMethod = 'microsoft';
                localStorage.setItem("ACCESS_TOKEN", JSON.stringify(data));
                localStorage.setItem("PRIVATE_URL", "npa");
                // Clean up URL and navigate
                window.history.replaceState({}, document.title, window.location.pathname);
                navigate("/");
            }

        } catch (error) {
            console.error('Microsoft login error:', error);
            setMsLoading(false);

            const errorMessage = error.response?.data?.error?.message || 'Microsoft login failed';

            swal({
                title: "Authentication failed",
                text: errorMessage,
                icon: "error",
                timer: 3000,
                buttons: false,
            });

            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    };

    const handleMicrosoftLogin = async () => {
        try {
            setMsLoading(true);

            // Generate PKCE code verifier and challenge
            const codeVerifier = generateCodeVerifier();
            const codeChallenge = await generateCodeChallenge(codeVerifier);

            // Store code verifier in session storage
            sessionStorage.setItem('pkce_code_verifier', codeVerifier);

            // Build authorization URL with PKCE parameters
            const authUrl = `https://login.microsoftonline.com/${MICROSOFT_CONFIG.tenantId}/oauth2/v2.0/authorize?` +
                `client_id=${MICROSOFT_CONFIG.clientId}` +
                `&response_type=code` +
                `&redirect_uri=${encodeURIComponent(MICROSOFT_CONFIG.redirectUri)}` +
                `&scope=${encodeURIComponent(MICROSOFT_CONFIG.scope)}` +
                `&code_challenge=${codeChallenge}` +
                `&code_challenge_method=S256` +
                `&response_mode=query` +
                `&prompt=select_account`; // Force account selection after logout

            console.log('Redirecting to Microsoft login with PKCE...');

            // Redirect to Microsoft login
            window.location.href = authUrl;
        } catch (error) {
            console.error('Error initiating Microsoft login:', error);
            setMsLoading(false);
            swal({
                title: "Login Error",
                text: "Failed to initiate Microsoft login",
                icon: "error",
                timer: 2000,
                buttons: false,
            });
        }
    };

    const handleSubmit = (values) => {
        setLoading(true);
        signin("npa", values["userName"], values["password"])
            .then((response) => {
                if (response) {
                    const data = response.data.data;
                    localStorage.setItem("ACCESS_TOKEN", JSON.stringify(data));
                    localStorage.setItem("PRIVATE_URL", "npa");
                    navigate("/");
                    setLoading(false);
                }
            })
            .catch((err) => {
                setLoading(false);
                if (err?.response?.data?.error) {
                    swal({
                        title: err.response.data.error.message,
                        icon: "error",
                        timer: 2000,
                        buttons: false,
                    });
                }
            });
    };

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f5f5f5',
            width: '100%'
        }}>
            <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
            }}>
                <div style={{
                    width: '100%',
                    maxWidth: '420px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    padding: '40px'
                }}>
                    <div style={{
                        textAlign: 'center',
                        marginBottom: '32px'
                    }}>
                        <img
                            style={{ width: "80px", height: "80px", objectFit: "cover", marginBottom: '16px' }}
                            src="/logo.png"
                            alt="Logo"
                        />
                        <h2 style={{
                            fontSize: '24px',
                            fontWeight: '600',
                            color: '#262626',
                            margin: '0 0 4px 0'
                        }}>
                            Norwegian People's Aid
                        </h2>
                        <h3 style={{
                            fontSize: '18px',
                            fontWeight: '500',
                            color: '#595959',
                            margin: '0 0 8px 0'
                        }}>
                            Cambodia
                        </h3>
                        <p style={{
                            fontSize: '13px',
                            color: '#8c8c8c',
                            margin: 0
                        }}>
                            {t("text_version")}
                        </p>
                    </div>

                    <Form form={form} onFinish={handleSubmit} layout="vertical">
                        <Button
                            type="default"
                            loading={msLoading}
                            onClick={handleMicrosoftLogin}
                            style={{
                                width: '100%',
                                height: '48px',
                                backgroundColor: '#2F5230',
                                border: 'none',
                                color: '#ffffff',
                                fontSize: '15px',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '12px',
                                borderRadius: '4px',
                                marginBottom: '24px'
                            }}
                            icon={
                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
                                    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                                    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                                    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                                </svg>
                            }
                        >
                            Sign in with Microsoft
                        </Button>

                        <Divider style={{ margin: '24px 0', fontSize: '13px', color: '#8c8c8c' }}>
                            Or continue with
                        </Divider>

                        <Form.Item
                            name="userName"
                            rules={[
                                {
                                    required: true,
                                    message: t("text_username_is_required"),
                                },
                            ]}
                            label={<span style={{ fontSize: '14px', fontWeight: '500' }}>{t("text_username")}</span>}
                        >
                            <Input
                                ref={userNameInputRef}
                                placeholder={t("text_please_enter_your_username")}
                                style={{
                                    height: '40px',
                                    fontSize: '14px'
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            rules={[
                                {
                                    required: true,
                                    message: t("text_password_is_required"),
                                },
                            ]}
                            label={<span style={{ fontSize: '14px', fontWeight: '500' }}>{t("text_password")}</span>}
                        >
                            <Input.Password
                                placeholder={t("text_password")}
                                style={{
                                    height: '40px',
                                    fontSize: '14px'
                                }}
                            />
                        </Form.Item>

                        <div style={{ textAlign: 'right', marginBottom: '24px' }}>
                            <Button
                                type="link"
                                style={{ padding: 0, fontSize: '13px', color: '#4F7942' }}
                                onClick={() => navigate('/forgot-password')}
                            >
                                Forgot Password?
                            </Button>
                        </div>

                        <Form.Item>
                            <Button
                                loading={loading}
                                style={{
                                    width: '100%',
                                    height: '40px',
                                    backgroundColor: '#4F7942',
                                    border: 'none',
                                    color: '#ffffff',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    borderRadius: '4px'
                                }}
                                type="primary"
                                htmlType="submit"
                            >
                                {t("text_log_in")}
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </div>

            <div style={{
                textAlign: 'center',
                padding: '20px',
                fontSize: '13px',
                color: '#8c8c8c'
            }}>
                <span>{t("text_ca_invention_copyright")}</span>
            </div>
        </div>
    );
}
